import { useMemo, useState } from "react";
import { ArrowUpDown, ChevronLeft, ChevronRight, Search } from "lucide-react";
import { Input } from "./ui/input";
import { Button } from "./ui/button";

export default function DataTable({
  columns, rows = [], searchKeys = [], searchPlaceholder = "Rechercher…",
  onRowClick, pageSize = 10, testidPrefix = "table", toolbar, emptyText = "Aucune donnée",
}) {
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState(null);
  const [page, setPage] = useState(0);

  const filtered = useMemo(() => {
    let out = [...rows];
    if (search && searchKeys.length) {
      const s = search.toLowerCase();
      out = out.filter((r) => searchKeys.some((k) => String(r[k] ?? "").toLowerCase().includes(s)));
    }
    if (sort) {
      out.sort((a, b) => {
        const va = a[sort.key];
        const vb = b[sort.key];
        if (va == null) return 1;
        if (vb == null) return -1;
        const cmp = typeof va === "number" && typeof vb === "number" ? va - vb : String(va).localeCompare(String(vb));
        return sort.dir === "asc" ? cmp : -cmp;
      });
    }
    return out;
  }, [rows, search, sort, searchKeys]);

  const pages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const cur = Math.min(page, pages - 1);
  const view = filtered.slice(cur * pageSize, (cur + 1) * pageSize);

  return (
    <div className="rounded-xl border border-slate-200 bg-white shadow-sm" data-testid={`${testidPrefix}-container`}>
      <div className="flex flex-wrap items-center gap-3 border-b border-slate-100 p-4">
        {searchKeys.length > 0 && (
          <div className="relative flex-1 min-w-48 max-w-sm">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input data-testid={`${testidPrefix}-search`} value={search} placeholder={searchPlaceholder}
              onChange={(e) => { setSearch(e.target.value); setPage(0); }} className="pl-9 bg-slate-50" />
          </div>
        )}
        <div className="ml-auto flex items-center gap-2">{toolbar}</div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-100 bg-slate-50/60 text-left">
              {columns.map((c) => (
                <th key={c.key} className={`whitespace-nowrap px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500 ${c.className || ""}`}>
                  {c.sortable === false ? (
                    c.label
                  ) : (
                    <button className="flex items-center gap-1 hover:text-slate-800"
                      onClick={() => setSort((s) => (s?.key === c.key ? { key: c.key, dir: s.dir === "asc" ? "desc" : "asc" } : { key: c.key, dir: "asc" }))}>
                      {c.label} <ArrowUpDown className="h-3 w-3" />
                    </button>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {view.length === 0 && (
              <tr><td colSpan={columns.length} className="px-4 py-10 text-center text-slate-400" data-testid={`${testidPrefix}-empty`}>{emptyText}</td></tr>
            )}
            {view.map((row, i) => (
              <tr key={row.id || i} onClick={() => onRowClick?.(row)}
                className={`border-b border-slate-50 last:border-0 ${onRowClick ? "cursor-pointer hover:bg-blue-50/40" : "hover:bg-slate-50/60"} transition-colors`}
                data-testid={`${testidPrefix}-row-${i}`}>
                {columns.map((c) => (
                  <td key={c.key} className={`px-4 py-3 ${c.className || ""}`}>
                    {c.render ? c.render(row) : String(row[c.key] ?? "—")}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {filtered.length > pageSize && (
        <div className="flex items-center justify-between border-t border-slate-100 px-4 py-3 text-sm text-slate-500">
          <span>{filtered.length} élément(s) — page {cur + 1}/{pages}</span>
          <div className="flex gap-1">
            <Button variant="outline" size="icon" disabled={cur === 0} onClick={() => setPage(cur - 1)} data-testid={`${testidPrefix}-prev-page`}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="icon" disabled={cur >= pages - 1} onClick={() => setPage(cur + 1)} data-testid={`${testidPrefix}-next-page`}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
